import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Global cycle filter (Option B / multi-active cycles).
 *
 * The user picks a reconciliation cycle in the AppShell top bar dropdown
 * and EVERY page reads the selection from this context to scope its
 * Convex queries. The default is "all" (no cycle filter), which means
 * pages see aggregated data across every cycle.
 *
 * Persisted to localStorage so refreshes remember the selection.
 */

const STORAGE_KEY = "altelix.cycleFilter";

/** Special sentinel for "no filter — show everything". */
export const ALL_CYCLES = "all" as const;

export type CycleFilterValue = string | typeof ALL_CYCLES;

interface CycleFilterContextShape {
  /** "all" or a Convex Id<"reconciliationCycles"> as a string. */
  selectedCycleId: CycleFilterValue;
  setSelectedCycleId: (next: CycleFilterValue) => void;
  /** True when the filter is currently set to ALL_CYCLES. */
  isAll: boolean;
  /**
   * Convenience: returns either `{ reconciliationCycleId }` to spread into a
   * Convex query args object, or `{}` when the filter is "all".
   * Use as: `useStableQuery(api.foo.bar, { ...cycleArg, otherArg })`.
   */
  cycleArg: { reconciliationCycleId?: string };
}

const CycleFilterContext = createContext<CycleFilterContextShape | null>(null);

function readInitial(): CycleFilterValue {
  if (typeof window === "undefined") return ALL_CYCLES;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length > 0) return stored;
  } catch {
    /* ignore */
  }
  return ALL_CYCLES;
}

export function CycleFilterProvider({ children }: { children: ReactNode }) {
  const [selectedCycleId, setSelectedCycleIdState] = useState<CycleFilterValue>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, selectedCycleId);
    } catch {
      /* ignore */
    }
  }, [selectedCycleId]);

  const value = useMemo<CycleFilterContextShape>(() => {
    const isAll = selectedCycleId === ALL_CYCLES;
    return {
      selectedCycleId,
      setSelectedCycleId: setSelectedCycleIdState,
      isAll,
      cycleArg: isAll ? {} : { reconciliationCycleId: selectedCycleId },
    };
  }, [selectedCycleId]);

  return <CycleFilterContext.Provider value={value}>{children}</CycleFilterContext.Provider>;
}

export function useCycleFilter(): CycleFilterContextShape {
  const ctx = useContext(CycleFilterContext);
  if (!ctx) throw new Error("useCycleFilter must be used within a CycleFilterProvider");
  return ctx;
}
